// KPI pages and dashboards (KD1). Mounted at /kpi/nba/* (App.tsx).
//
//   /kpi/nba                                   dashboard home: Team | Players, a game, a window
//   /kpi/nba/player/:playerId                  one player's dashboard page (window from the query string)
//   /kpi/nba/packet/:gameId/team               Team KPI page, print (postgame packet page 3)
//   /kpi/nba/packet/:gameId/players            Player KPI page, print (postgame packet page 4)
//   /kpi/nba/packet/:gameId/player/:playerId   one player's page for that game, print
//   /kpi/nba/packet/:gameId/rebounding         Rebounding (crashing) page, print (postgame packet page 5)
//   /kpi/nba/rebounding                        the same page with a game picker (KPIs menu: Rebounding)
//   /kpi/nba/live                              Live KPIs: a game picker and the selected game's live sheets (LivePage)
//   /kpi/nba/live/:gameId                      team + player KPIs during a game (each completed period), screen only
//
// Every page's root (.kpi-doc) carries the automation contract: data-report-ready = false | true | pending | error
// (common.tsx useKpiReady). `?targets=<season>` replays a past game against another season's KPI list.
import { useCallback, useRef, useState } from "react";
import {
  Link,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Pending, useKpiData, useKpiReady } from "./common";
import KpiHome from "./KpiHome";
import LivePage from "./LivePage";
import PlayerKpiPage from "./PlayerKpiPage";
import PlayerPage from "./PlayerPage";
import ReboundingPacketPage, { ReboundingSitePage } from "./ReboundingKpiPage";
import TeamKpiPage from "./TeamKpiPage";
import type { KpiResponse } from "./types";
import "./kpi.scss";

const BASE = "/kpi/nba";

function withTargets(path: string, params: URLSearchParams) {
  const t = params.get("targets");
  return t
    ? `${path}${path.includes("?") ? "&" : "?"}targets=${encodeURIComponent(t)}`
    : path;
}

// The player page still too tall at its densest step: the packet delivers it with a warning, not silently clipped.
const OVERFLOW = ["overflow"];

function PacketPage({ kind }: { kind: "team" | "players" | "player" }) {
  const { gameId = "", playerId = "" } = useParams();
  const [params] = useSearchParams();
  const load = useKpiData<KpiResponse>(
    withTargets(`api/kpi/packet/${encodeURIComponent(gameId)}`, params),
  );
  const root = useRef<HTMLDivElement>(null);
  const [fitted, setFitted] = useState(kind !== "players");
  const [overflow, setOverflow] = useState(false);
  const onSettled = useCallback((s: boolean, o: boolean) => {
    setFitted(s);
    setOverflow(o);
  }, []);
  const ready = useKpiReady(root, load, !fitted, overflow ? OVERFLOW : []);
  const data = load.data;
  const title = {
    team: "Team KPIs",
    players: "Player KPIs",
    player: "Player page",
  }[kind];
  const qs = params.toString() ? `?${params.toString()}` : "";
  return (
    <div className="kpi-doc" ref={root} {...ready}>
      <div className="kpi-webbar">
        <Link to={BASE} style={{ fontWeight: 700 }}>
          ← KPI dashboard
        </Link>
        {kind === "player" && (
          <Link
            to={`${BASE}/packet/${gameId}/players${qs}`}
            style={{ fontWeight: 700 }}
          >
            ← Player KPIs · full roster
          </Link>
        )}
      </div>
      {load.error && (
        <Pending
          title={title}
          reason={`Could not load the KPIs: ${load.error}`}
        />
      )}
      {data?.status?.state === "pending" && (
        <Pending title={title} reason={data.status.reason} />
      )}
      {data?.status?.state === "ready" && data.meta.game && (
        <>
          {kind === "team" && <TeamKpiPage data={data} />}
          {kind === "players" && (
            // No card links on the packet's print route: they'd be link annotations in the PDF (Trevor 2026-10-11).
            <PlayerKpiPage data={data} onSettled={onSettled} />
          )}
          {kind === "player" && (
            <PlayerPage data={data} playerId={decodeURIComponent(playerId)} />
          )}
        </>
      )}
    </div>
  );
}

export default function Kpi() {
  return (
    <Routes>
      <Route index element={<KpiHome />} />
      <Route path="player/:playerId" element={<KpiHome single />} />
      <Route path="packet/:gameId/team" element={<PacketPage kind="team" />} />
      <Route
        path="packet/:gameId/players"
        element={<PacketPage kind="players" />}
      />
      <Route
        path="packet/:gameId/player/:playerId"
        element={<PacketPage kind="player" />}
      />
      <Route
        path="packet/:gameId/rebounding"
        element={<ReboundingPacketPage />}
      />
      <Route path="rebounding" element={<ReboundingSitePage />} />
      <Route path="live" element={<LivePage />} />
      <Route path="live/:gameId" element={<LivePage />} />
    </Routes>
  );
}
